// kernel/useWorkspaces.ts
import { useCallback, RefObject, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import anime from 'animejs';
import { toast } from 'sonner';

import { KernelHandle, useWorkspacesReturnType, Workspace, Rect, Layer, ResizeHandleType, CanvasResizeState, AspectRatio } from  '#types/types';
import { getWorkspaceRasterDimensions } from '@/components/workspaceRasterUtils';
import { sortLayersByRenderOrder } from '#lib/layer-render-order';
import { requireWorkspaceProjectIdFromKernel } from '#lib/workspace-scope';
import { getProjectPanOffset, getProjectZoomLevel, setProjectPanOffset } from '#lib/project-runtime-layer-ui-state';
import { getDrawingAwareSvgLayerSource, getDrawingLayerPayload } from '#lib/drawing-layer';

interface UseWorkspacesProps {
  kernel: RefObject<KernelHandle | null>;
}

const LAYOUT_CONFIG = {
  GROUP_GAP_FACTOR: 0.25,
  WORKSPACE_VERTICAL_GAP_FACTOR: 0.25,
};

const createLayerId = (): string => `layer-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const DUPLICATE_MERGE_MAX_SVG_RASTER_SIDE = 8192;

const loadDuplicateMergeImage = (src: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Failed to load SVG layer for Duplicate & Merge.'));
  image.src = src;
});

const getPositiveLayerRectSize = (layer: Layer): { w: number; h: number } => {
  const rectW = typeof layer.rect?.w === 'number' && Number.isFinite(layer.rect.w) && layer.rect.w > 0 ? layer.rect.w : 0;
  const rectH = typeof layer.rect?.h === 'number' && Number.isFinite(layer.rect.h) && layer.rect.h > 0 ? layer.rect.h : 0;
  return { w: rectW, h: rectH };
};

const isDuplicateMergeVectorLayer = (layer: Layer): boolean => (
  layer.type === 'svg' || Boolean(getDrawingLayerPayload(layer.params))
);

const rasterizeSvgLayerForDuplicateMerge = async (layer: Layer): Promise<Layer | null> => {
  if (!isDuplicateMergeVectorLayer(layer)) return null;
  const svgSource = getDrawingAwareSvgLayerSource(layer);
  if (!svgSource) return null;

  const image = await loadDuplicateMergeImage(svgSource);
  const rectSize = getPositiveLayerRectSize(layer);
  const displayWidth = rectSize.w || image.naturalWidth || image.width || 1;
  const displayHeight = rectSize.h || image.naturalHeight || image.height || 1;
  const longestDisplaySide = Math.max(displayWidth, displayHeight, 1);
  const rasterScale = Math.min(1, DUPLICATE_MERGE_MAX_SVG_RASTER_SIDE / longestDisplaySide);
  const rasterWidth = Math.max(1, Math.round(displayWidth * rasterScale));
  const rasterHeight = Math.max(1, Math.round(displayHeight * rasterScale));

  const canvas = new OffscreenCanvas(rasterWidth, rasterHeight);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(image, 0, 0, rasterWidth, rasterHeight);

  return {
    ...layer,
    imageData: ctx.getImageData(0, 0, rasterWidth, rasterHeight),
    imageSrc: undefined,
    rect: {
      ...layer.rect,
      w: displayWidth,
      h: displayHeight,
    },
    scale: displayWidth / rasterWidth,
  };
};

const areStringArraysEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
};
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);
const normalizeRectValue = (value: unknown): Rect | null => {
  if (!value || typeof value !== 'object') return null;
  const x = Reflect.get(value, 'x');
  const y = Reflect.get(value, 'y');
  const w = Reflect.get(value, 'w');
  const h = Reflect.get(value, 'h');
  if (
    !isFiniteNumber(x) ||
    !isFiniteNumber(y) ||
    !isFiniteNumber(w) ||
    !isFiniteNumber(h)
  ) {
    return null;
  }
  return { x, y, w, h };
};

export const useWorkspaces = ({ kernel }: UseWorkspacesProps): useWorkspacesReturnType => {
  const normalizeWorkspaceIdentifier = useCallback((value: unknown): string => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return '';
  }, []);

  const getLayersByWorkspaceId = useCallback((workspaceId: string): Layer[] => {
    const k = kernel.current;
    if (!k) return [];
    const layerRegistry = k.getLayerRegistry();
    const workspace = k.getInventory().StudioWorkspace.registry[workspaceId];
    const workspaceLayerIds = workspace?.layerIds ?? [];
    if (workspaceLayerIds.length === 0) return [];
    return workspaceLayerIds
      .map((layerId) => layerRegistry[layerId])
      .filter((layer): layer is NonNullable<typeof layer> => Boolean(layer));
  }, [kernel]);

  const getAllProjectWorkspaces = useCallback((projectId: string): Workspace[] => {
    const k = kernel.current;
    if (!k) return [];
    const project = k.getInventory().Project.registry[projectId];
    const workspaceIds = Array.isArray(project?.workspaceIds)
      ? project.workspaceIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
      : [];
    return workspaceIds
      .map((workspaceId) => k.getWorkspace(workspaceId))
      .filter((workspace): workspace is Workspace => Boolean(workspace));
  }, [kernel]);

  const getProjectUIWorkspaceId = useCallback<KernelHandle['getProjectUIWorkspaceId']>((projectId) => {
    const k = kernel.current;
    if (!k) return '';

    const normalizedProjectId = projectId.trim();
    if (!normalizedProjectId) return '';

    const mergedInventory = k.getInventory();
    const projectRow = mergedInventory.Project.registry[normalizedProjectId];
    const workspaceRegistry = mergedInventory.StudioWorkspace.registry;
    const projectWorkspaceIds = Array.isArray(projectRow?.workspaceIds)
      ? projectRow.workspaceIds.filter(
          (workspaceId): workspaceId is string =>
            typeof workspaceId === 'string' &&
            workspaceId.trim().length > 0 &&
            Boolean(workspaceRegistry[workspaceId])
        )
      : [];
    const projectWorkspaceIdsSet = new Set(projectWorkspaceIds);
    const fallbackWorkspaceId = projectWorkspaceIds[0] ?? '';

    const projectRuntime = k.readProjectRuntimeState(normalizedProjectId);
    const runtimeWorkspaceId = typeof projectRuntime.UIWorkspaceId === 'string'
      ? projectRuntime.UIWorkspaceId.trim()
      : '';
    if (
      runtimeWorkspaceId &&
      projectWorkspaceIdsSet.has(runtimeWorkspaceId) &&
      workspaceRegistry[runtimeWorkspaceId]
    ) {
      return runtimeWorkspaceId;
    }

    const mergedProjectState = k.readProjectKernelMergedState(normalizedProjectId);
    if (!isRecord(mergedProjectState)) return fallbackWorkspaceId;
    const persistedWorkspaceId = typeof mergedProjectState.activeLayerWorkspaceId === 'string'
      ? mergedProjectState.activeLayerWorkspaceId.trim()
      : '';
    if (
      persistedWorkspaceId &&
      projectWorkspaceIdsSet.has(persistedWorkspaceId) &&
      workspaceRegistry[persistedWorkspaceId]
    ) {
      return persistedWorkspaceId;
    }

    return fallbackWorkspaceId;
  }, [kernel]);

  const setProjectUIWorkspaceId = useCallback<KernelHandle['setProjectUIWorkspaceId']>((projectId, workspaceId) => {
    const k = kernel.current;
    if (!k) return;
    const normalizedProjectId = projectId.trim();
    if (!normalizedProjectId) return;
    const normalizedWorkspaceId = workspaceId.trim();
    k.patchProjectRuntimeState(normalizedProjectId, {
      UIWorkspaceId: normalizedWorkspaceId || undefined,
    });
  }, [kernel]);

  const readWorkspaceAnimatedGenerationRect = useCallback((workspaceId: string): Rect | null => {
    const k = kernel.current;
    if (!k) return null;
    const normalizedWorkspaceId = typeof workspaceId === 'string' ? workspaceId.trim() : '';
    if (!normalizedWorkspaceId) return null;
    const workspace = k.getInventory().StudioWorkspace.registry[normalizedWorkspaceId];
    if (!workspace) return null;
    return normalizeRectValue(workspace.animatedGenerationRect);
  }, [kernel]);

  const setWorkspaceAnimatedGenerationRect = useCallback<KernelHandle['setWorkspaceAnimatedGenerationRect']>((workspaceId, updater) => {
    const k = kernel.current;
    if (!k) return;
    const normalizedWorkspaceId = typeof workspaceId === 'string' ? workspaceId.trim() : '';
    if (!normalizedWorkspaceId) return;
    if (!k.getInventory().StudioWorkspace.registry[normalizedWorkspaceId]) return;
    const previousValue = readWorkspaceAnimatedGenerationRect(normalizedWorkspaceId);
    const nextRaw = typeof updater === 'function' ? updater(previousValue) : updater;
    const nextValue = normalizeRectValue(nextRaw);
    k.patchEntityRuntimeState('StudioWorkspace', normalizedWorkspaceId, {
      animatedGenerationRect: nextValue,
    });
  }, [kernel, readWorkspaceAnimatedGenerationRect]);

  const loadWorkspace = useCallback((workspaceInput: unknown) => {
    const ws = (workspaceInput ?? {}) as Record<string, unknown>;
    const workspaceId = normalizeWorkspaceIdentifier(ws.id);
    if (!workspaceId) {
      throw new Error('[useWorkspaces.loadWorkspace] Missing explicit workspace.id.');
    }

    const projectId =
      typeof ws.projectId === 'string' && ws.projectId.trim().length > 0
        ? ws.projectId.trim()
        : '';
    if (!projectId) {
      throw new Error(`[useWorkspaces.loadWorkspace] Missing explicit projectId for workspace "${workspaceId}".`);
    }

    const hasValidRect = (rect: unknown): rect is Rect => {
      if (!rect || typeof rect !== 'object') return false;
      const candidate = rect as Rect;
      return (
        Number.isFinite(candidate.x) &&
        Number.isFinite(candidate.y) &&
        Number.isFinite(candidate.w) &&
        candidate.w > 0 &&
        Number.isFinite(candidate.h) &&
        candidate.h > 0
      );
    };

    if (!hasValidRect(ws.generationRect)) {
      throw new Error(
        `[useWorkspaces.loadWorkspace] Missing/invalid explicit generationRect for workspace "${workspaceId}".`
      );
    }
    if (!hasValidRect(ws.frameRect)) {
      throw new Error(
        `[useWorkspaces.loadWorkspace] Missing/invalid explicit frameRect for workspace "${workspaceId}".`
      );
    }

    if (!Array.isArray(ws.layerIds)) {
      throw new Error(
        `[useWorkspaces.loadWorkspace] Missing/invalid explicit layerIds for workspace "${workspaceId}".`
      );
    }
    const layerIds = Array.from(
      new Set(
        ws.layerIds
          .map((layerId) => normalizeWorkspaceIdentifier(layerId))
          .filter((layerId) => layerId.length > 0)
      )
    );

    const aspectRatioValue = ws.aspectRatio;
    const normalizedAspectRatio: AspectRatio =
      typeof aspectRatioValue === 'number' && Number.isFinite(aspectRatioValue)
        ? aspectRatioValue
        : null;

    const loadedWorkspace: Workspace = {
      ...(ws as Partial<Workspace>),
      id: workspaceId,
      projectId,
      name: typeof ws.name === 'string' && ws.name.trim().length > 0 ? ws.name : workspaceId,
      layerIds,
      activeVersionId:
        typeof ws.activeVersionId === 'string' && ws.activeVersionId.trim().length > 0
          ? ws.activeVersionId
          : (typeof ws.validatedVersionId === 'string' && ws.validatedVersionId.trim().length > 0
            ? ws.validatedVersionId
            : null),
      generationRect: ws.generationRect as Rect,
      frameRect: ws.frameRect as Rect,
      visible: ws.visible !== undefined ? Boolean(ws.visible) : true,
      annotationsEnabled: ws.annotationsEnabled !== false,
      annotationOpaque: ws.annotationOpaque === true,
      annotationMask: null,
      annotationMaskVersionId: null,
      expanded: ws.expanded !== undefined ? Boolean(ws.expanded) : true,
      locked: ws.locked !== undefined ? Boolean(ws.locked) : true,
      aspectRatio: normalizedAspectRatio,
      validatedVersionId:
        typeof ws.validatedVersionId === 'string' && ws.validatedVersionId.trim().length > 0
          ? ws.validatedVersionId
          : null,
      versionFilterMode: 'PRIMARY',
    };

    return {
      workspace: loadedWorkspace,
    };
  }, [normalizeWorkspaceIdentifier]);

  const syncProjectWorkspaceIdsDraft = useCallback((projectIdInput: string, workspaceIds: string[]) => {
    const k = kernel.current;
    if (!k) return;

    const projectId = projectIdInput.trim();
    if (!projectId) return;

    const mergedProject = k.readProjectKernelMergedState(projectId);
    const currentWorkspaceIds = Array.isArray(mergedProject?.workspaceIds)
      ? mergedProject.workspaceIds.filter((workspaceId): workspaceId is string => typeof workspaceId === 'string')
      : [];

    if (areStringArraysEqual(currentWorkspaceIds, workspaceIds)) {
      return;
    }

    k.mergeEntityKernelState('Project', projectId, { workspaceIds });
  }, [kernel]);

  const buildWorkspaceMutationPayload = useCallback((workspace: Workspace) => ({
    projectId: workspace.projectId,
    name: typeof workspace.name === 'string' && workspace.name.trim().length > 0 ? workspace.name.trim() : workspace.id,
    visible: workspace.visible !== false,
    generationRect: workspace.generationRect,
    frameRect: workspace.frameRect,
    aspectRatio: typeof workspace.aspectRatio === 'number' ? workspace.aspectRatio : null,
    annotationsEnabled: workspace.annotationsEnabled !== false,
    annotationOpaque: workspace.annotationOpaque === true,
    lastMaskUrl: typeof workspace.lastMaskUrl === 'string' ? workspace.lastMaskUrl : null,
    cameraState: workspace.cameraState ?? null,
    validatedVersionId:
      typeof workspace.validatedVersionId === 'string' && workspace.validatedVersionId.trim().length > 0
        ? workspace.validatedVersionId
        : null,
    layerIds: Array.isArray(workspace.layerIds)
      ? workspace.layerIds.filter((layerId): layerId is string => typeof layerId === 'string' && layerId.trim().length > 0)
      : [],
    versionIds: Array.isArray(workspace.versions)
      ? workspace.versions
          .map((version) => (typeof version?.id === 'string' ? version.id.trim() : ''))
          .filter((versionId): versionId is string => versionId.length > 0)
      : [],
  }), []);

  // Settles once the row exists on the server; a caller that does not wait still gets the failure logged.
  const persistWorkspaceCreate = useCallback((workspace: Workspace, context: string): Promise<void> => {
    const workspaceId = typeof workspace.id === 'string' ? workspace.id.trim() : '';
    const written = (async () => {
      const k = kernel.current;
      const projectId = typeof workspace.projectId === 'string' ? workspace.projectId.trim() : '';
      if (!k || !workspaceId || !projectId) throw new Error('Cannot persist workspace create: missing kernel, workspaceId or projectId.');

      const payload = buildWorkspaceMutationPayload(workspace);

      await k.queueStudioWrite(
        [{
          action: 'CREATE',
          entity: 'StudioWorkspace',
          id: workspaceId,
          payload,
        }] as any
      );
    })();
    written.catch((error) => {
      console.error(`[${context}] Failed to persist created workspace "${workspaceId}".`, error);
    });
    return written;
  }, [kernel, buildWorkspaceMutationPayload]);

  const persistDuplicatedWorkspaceBundle = useCallback((
    workspaces: Workspace[],
    context: string
  ) => {
    const k = kernel.current;
    if (!k) return;

    const workspaceCreateOperations = workspaces
      .filter((workspace) => typeof workspace.id === 'string' && workspace.id.trim().length > 0)
      .map((workspace) => ({
        action: 'CREATE',
        entity: 'StudioWorkspace',
        id: workspace.id,
        payload: buildWorkspaceMutationPayload(workspace),
      }));

    if (workspaceCreateOperations.length === 0) return;

    // The layers are created by the saves setLayer queued, which reach the write queue after these.
    void k.queueStudioWrite(
      workspaceCreateOperations as any
    ).catch((error) => {
      console.error(`[${context}] Failed to persist duplicated workspace bundle.`, error);
    });
  }, [kernel, buildWorkspaceMutationPayload]);

  const setWorkspaceFrameRect = useCallback((workspaceId: string, newRect: Rect, oldRectOverride?: Rect) => {
    if (!kernel.current) return;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useWorkspaces');
    let annotationVersionIdToPersist: string | null = null;
    let shouldPersistAnnotation = false;

    kernel.current.setWorkspace(workspaceId, (workspace) => {
      if (!workspace) return null;
      annotationVersionIdToPersist =
        typeof workspace.activeVersionId === 'string' && workspace.activeVersionId.trim().length > 0
          ? workspace.activeVersionId
          : workspace.annotationMaskVersionId ?? null;

      // CRITICAL FIX: Use the override if provided (from MouseUp), otherwise use current state
      const oldFrameRect = oldRectOverride || workspace.frameRect;
      
      const dx = newRect.x - oldFrameRect.x;
      const dy = newRect.y - oldFrameRect.y;

      // If nothing changed effectively, return early
      if (dx === 0 && dy === 0 && newRect.w === oldFrameRect.w && newRect.h === oldFrameRect.h) {
         return workspace;
      }
      shouldPersistAnnotation = true;

      let newAnnotationMask: ImageData | null = null;
      const oldAnnotationMask = workspace.annotationMask;

      // 1. Calculate dimensions for the new mask based on the new frame size
      const { width: newMaskWidth, height: newMaskHeight, scale: newMaskScale } = getWorkspaceRasterDimensions(newRect);
      
      if (oldAnnotationMask) {
        const { scale: oldMaskScale } = getWorkspaceRasterDimensions(oldFrameRect);

        const newMaskCanvas = document.createElement('canvas');
        newMaskCanvas.width = newMaskWidth;
        newMaskCanvas.height = newMaskHeight;
        const newMaskCtx = newMaskCanvas.getContext('2d');

        if (newMaskCtx) {
          const oldMaskCanvas = document.createElement('canvas');
          oldMaskCanvas.width = oldAnnotationMask.width;
          oldMaskCanvas.height = oldAnnotationMask.height;
          oldMaskCanvas.getContext('2d')?.putImageData(oldAnnotationMask, 0, 0);

          const translateX = -dx * newMaskScale;
          const translateY = -dy * newMaskScale;
          const scaleRatio = newMaskScale / oldMaskScale;

          newMaskCtx.translate(translateX, translateY);
          newMaskCtx.scale(scaleRatio, scaleRatio);
          newMaskCtx.imageSmoothingEnabled = false;
          
          newMaskCtx.drawImage(oldMaskCanvas, 0, 0);
          newAnnotationMask = newMaskCtx.getImageData(0, 0, newMaskWidth, newMaskHeight);
        }
      } else {
        newAnnotationMask = new ImageData(newMaskWidth, newMaskHeight);
      }

      return {
        ...workspace,
        frameRect: newRect,
        annotationMask: newAnnotationMask,
        annotationMaskVersionId:
          typeof workspace.activeVersionId === 'string' && workspace.activeVersionId.trim().length > 0
            ? workspace.activeVersionId
            : workspace.annotationMaskVersionId ?? null,
        versions: Array.isArray(workspace.versions)
          ? workspace.versions.map((version) => {
              if (version.id !== workspace.activeVersionId) return version;
              return {
                ...version,
                annotationMask: newAnnotationMask,
                annotationMaskIsDirty: true,
              };
            })
          : workspace.versions,
      };
    }, projectId);

    if (shouldPersistAnnotation && annotationVersionIdToPersist) {
      void kernel.current.saveWorkspaceVersionAnnotation(workspaceId, annotationVersionIdToPersist).catch((error) => {
        console.error(`[useWorkspaces.setWorkspaceFrameRect] Failed to persist annotation mask for version "${annotationVersionIdToPersist}".`, error);
      });
    }
    
  }, [kernel]);

  const findSmartPlacementPosition = useCallback((
    projectId: string,
    newWorkspace: { frameRect: Rect },
    pendingRects: Rect[] = []
  ): Rect => {
    if (!kernel.current) return newWorkspace.frameRect;

    const allWorkspaces = getAllProjectWorkspaces(projectId);
    const existingRects: Rect[] = [
      ...allWorkspaces.map(ws => ws.frameRect),
      ...pendingRects,
    ];

    let idealX: number;
    let idealY: number;

    // 1. Determine the IDEAL position based on hierarchy
    // --- This is a NEW ROOT workspace ---
    if (pendingRects.length > 0) {
      // This is part of a multi-creation, stack it vertically
      const lastPendingRect = pendingRects[pendingRects.length - 1]!;
      const verticalGap = lastPendingRect.h * LAYOUT_CONFIG.WORKSPACE_VERTICAL_GAP_FACTOR;
      idealX = lastPendingRect.x;
      idealY = lastPendingRect.y + lastPendingRect.h + verticalGap;
    } else {
      // This is a single new root workspace, place it to the right of everything
      if (existingRects.length === 0) {
        return newWorkspace.frameRect; // First item on canvas
      }
      const worldBbox = existingRects.reduce((bbox, rect) => ({
        minX: Math.min(bbox.minX, rect.x),
        minY: Math.min(bbox.minY, rect.y),
        maxX: Math.max(bbox.maxX, rect.x + rect.w),
        maxY: Math.max(bbox.maxY, rect.y + rect.h),
      }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
      
      idealX = worldBbox.maxX + (worldBbox.maxY - worldBbox.minY) * LAYOUT_CONFIG.GROUP_GAP_FACTOR;
      idealY = worldBbox.minY;
    }

    // 2. Check for collisions and nudge if necessary
    let finalRect = { ...newWorkspace.frameRect, x: idealX, y: idealY };
    let collision = true;
    let attempts = 0;
    const maxAttempts = 100;

    while (collision && attempts < maxAttempts) {
      collision = false;
      let rightmostCollisionEdge = -Infinity;

      for (const rect of existingRects) {
        const collides = (
          finalRect.x < rect.x + rect.w &&
          finalRect.x + finalRect.w > rect.x &&
          finalRect.y < rect.y + rect.h &&
          finalRect.y + finalRect.h > rect.y
        );
        if (collides) {
          collision = true;
          rightmostCollisionEdge = Math.max(rightmostCollisionEdge, rect.x + rect.w);
        }
      }

      if (collision) {
        finalRect.x = rightmostCollisionEdge + 50; // Use a fixed gap for nudging
      }
      attempts++;
    }

    return finalRect;
  }, [kernel, getAllProjectWorkspaces]);

  const handleAddWorkspace = useCallback<useWorkspacesReturnType['handleAddWorkspace']>((projectIdInput, options) => {
    if (!kernel.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;
    const currentWorkspaces = getAllProjectWorkspaces(projectIdInput);
    const activeWorkspace = currentWorkspaces.find(
      (ws) => ws.id === kernel.current?.getProjectUIWorkspaceId(projectId)
    );

    const requestedFrameSize = options?.frameSize;
    const requestedFrameWidth = typeof requestedFrameSize?.width === 'number' && Number.isFinite(requestedFrameSize.width)
      ? requestedFrameSize.width
      : null;
    const requestedFrameHeight = typeof requestedFrameSize?.height === 'number' && Number.isFinite(requestedFrameSize.height)
      ? requestedFrameSize.height
      : null;
    let baseRect: Rect;
    if (requestedFrameWidth && requestedFrameWidth > 0 && requestedFrameHeight && requestedFrameHeight > 0) {
      baseRect = { x: 0, y: 0, w: requestedFrameWidth, h: requestedFrameHeight };
    } else if (activeWorkspace) {
      baseRect = activeWorkspace.frameRect;
    } else {
      baseRect = { x: 0, y: 0, w: 720, h: 720 };
    }

    const newId = uuidv4().substring(0, 8);
    
    // Fallback for old projects: find max existing numeric name and start from there.
    const maxExistingName = currentWorkspaces
      .map(ws => parseInt(ws.name, 10))
      .filter(num => !isNaN(num))
      .reduce((max, num) => Math.max(max, num), 0);
    const projectRecord = kernel.current.getInventory().Project.registry[projectId] as Record<string, unknown> | undefined;
    const projectCounterRaw = projectRecord?.workspaceCounter;
    const projectCounter = typeof projectCounterRaw === 'number' && Number.isFinite(projectCounterRaw)
      ? projectCounterRaw
      : 0;
    let nextCount = Math.max(projectCounter, maxExistingName);
    let newName: string;
    
    do {
      nextCount++;
      newName = String(nextCount);
    } while (currentWorkspaces.some(ws => ws.name === newName));

    if (nextCount > projectCounter) {
      kernel.current.mergeEntityKernelState('Project', projectId, {
        workspaceCounter: nextCount,
      });
    }

    const tempNewWorkspace = {
      frameRect: { x: 0, y: 0, w: baseRect.w, h: baseRect.h }
    };
    const newFrameRect = findSmartPlacementPosition(projectIdInput, tempNewWorkspace);
    const newAspectRatio = newFrameRect.w > 0 && newFrameRect.h > 0 ? newFrameRect.w / newFrameRect.h : 1;

    const newWorkspace: Workspace = {
      id: newId,
      projectId,
      name: options?.name?.trim() || newName,
      layerIds: [],
      activeVersionId: null,
      generationRect: { ...newFrameRect },
      frameRect: newFrameRect,
      annotationMask: null,
      annotationsEnabled: true,
      annotationOpaque: false,
      expanded: true,
      locked: true,
      visible: true,
      aspectRatio: newAspectRatio,
      validatedVersionId: null,
      versionFilterMode: 'PRIMARY'
    };

    kernel.current?.logUserAction('Workspace Added', {
      new_workspace_id: newId,
      new_workspace_name: newName,
      dimensions_from: options?.logSource || (activeWorkspace ? activeWorkspace.id : 'default'),
    });

    syncProjectWorkspaceIdsDraft(projectId, [...currentWorkspaces.map((workspace) => workspace.id), newId]);
    kernel.current?.setWorkspace(newId, newWorkspace, projectId);
    // Outside the optional call: `options?.onPersisted?.(write())` skips write() when no callback is passed.
    const written = persistWorkspaceCreate(newWorkspace, 'useWorkspaces.handleAddWorkspace');
    options?.onPersisted?.(written);
    if (options?.focus !== false) {
      kernel.current?.setProjectUIWorkspaceId(projectId, newId);
    }
    if (options?.zoomToFit !== false) {
      kernel.current?.zoomToFitRectInViewport(projectId, newFrameRect, 1.2, kernel.current.getSpectacleMode());
    }
    return newId;
  }, [kernel, findSmartPlacementPosition, getAllProjectWorkspaces, syncProjectWorkspaceIdsDraft, persistWorkspaceCreate]);


  const renameWorkspace = useCallback((workspaceId: string, newName: string) => {
    if (!kernel.current) return;
    const trimmedName = newName.trim();
    if (!trimmedName) return;

    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useWorkspaces');
    const currentWorkspace = kernel.current.getWorkspace(workspaceId);
    const previousName = typeof currentWorkspace?.name === 'string' ? currentWorkspace.name : '';

    kernel.current.setWorkspace(workspaceId, (workspace) => ({
      ...workspace,
      name: trimmedName,
    }), projectId);

    if (previousName.trim() !== trimmedName) {
      kernel.current.logUserAction('Workspace Renamed', {
        workspace_id: workspaceId,
        old_name: previousName,
        new_name: trimmedName,
        source: 'workspace_setWorkspace_dto_autosave',
      });
    }
  }, [kernel]);

  const deleteWorkspace = useCallback((projectIdInput: string, workspaceId: string) => {
    if (!kernel.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;

    const currentWorkspaces = getAllProjectWorkspaces(projectIdInput);

    if (currentWorkspaces.length <= 1) {
      kernel.current?.handleError("Cannot delete the last workspace.");
      return;
    }

    const workspaceToDelete = currentWorkspaces.find(ws => ws.id === workspaceId);
    if (!workspaceToDelete) {
      kernel.current?.handleError(`Workspace with ID "${workspaceId}" not found for deletion.`);
      return;
    }

    const { getProjectUIWorkspaceId, setProjectUIWorkspaceId, logUserAction, executeAction } = kernel.current;

    const initialWorkspaces = getAllProjectWorkspaces(projectIdInput);
    const initialUIWorkspaceId = getProjectUIWorkspaceId(projectId);
    const initialWorkspaceIds = initialWorkspaces.map((workspace) => workspace.id);
    const initialWorkspaceLayers = getLayersByWorkspaceId(workspaceId).map((layer) => ({ ...layer }));

    const performDelete = () => {
      const currentWorkspaces = getAllProjectWorkspaces(projectIdInput);
      const workspaceToDelete = currentWorkspaces.find(ws => ws.id === workspaceId);
      if (!workspaceToDelete) return; // Already deleted or something went wrong.

      const runtimeBeforeDelete = kernel.current?.readProjectRuntimeState(projectId);
      const runtimeUIWorkspaceIdBeforeDelete =
        typeof runtimeBeforeDelete?.UIWorkspaceId === 'string'
          ? runtimeBeforeDelete.UIWorkspaceId.trim()
          : '';
      const resolvedUIWorkspaceIdBeforeDelete = getProjectUIWorkspaceId(projectId);
      const activeWorkspaceIdBeforeDelete =
        runtimeUIWorkspaceIdBeforeDelete || resolvedUIWorkspaceIdBeforeDelete;

      let newActiveWorkspaceId = activeWorkspaceIdBeforeDelete;

      // --- Simple Deletion Logic ---
      if (activeWorkspaceIdBeforeDelete === workspaceId) {
        const remainingWorkspaceIds = currentWorkspaces
          .filter((workspace) => workspace.id !== workspaceId)
          .map((workspace) => workspace.id)
          .sort((left, right) => left.localeCompare(right));
        if (remainingWorkspaceIds.includes('1')) {
          newActiveWorkspaceId = '1';
        } else {
          newActiveWorkspaceId = remainingWorkspaceIds[0] ?? '';
        }
      }
      
      kernel.current?.setWorkspace(workspaceId, null, projectId);
      syncProjectWorkspaceIdsDraft(
        projectId,
        currentWorkspaces
          .filter((workspace) => workspace.id !== workspaceId)
          .map((workspace) => workspace.id)
      );
      
      logUserAction('Workspace Deleted', { 
        deleted_workspace_id: workspaceId, 
        new_active_workspace_id: newActiveWorkspaceId 
      });

      // Persist a valid active workspace id in project runtime state to avoid stale selectors.
      const persistedUIWorkspaceId =
        newActiveWorkspaceId ||
        runtimeUIWorkspaceIdBeforeDelete ||
        resolvedUIWorkspaceIdBeforeDelete;
      setProjectUIWorkspaceId(projectId, persistedUIWorkspaceId);
      void kernel.current?.queueStudioWrite(
        [{
          action: 'UPDATE',
          entity: 'StudioWorkspace',
          id: workspaceId,
          payload: {
            projectId: null,
          },
        }] as any
      ).catch((error) => {
        console.error(`[useWorkspaces.deleteWorkspace] Failed to detach workspace "${workspaceId}".`, error);
      });
    };

    executeAction({
      description: `Delete workspace "${workspaceToDelete.name}"`,
      redo: performDelete,
      undo: () => {
        if (!kernel.current) return;
        const restoredWorkspacePayload = buildWorkspaceMutationPayload(workspaceToDelete);
        const restoreVersionIds = Array.from(
          new Set(
            Array.isArray(workspaceToDelete.versions)
              ? workspaceToDelete.versions
                  .map((version) => (typeof version?.id === 'string' ? version.id.trim() : ''))
                  .filter((versionId): versionId is string => versionId.length > 0)
              : []
          )
        );
        syncProjectWorkspaceIdsDraft(projectId, initialWorkspaceIds);
        kernel.current.setWorkspace(workspaceToDelete.id, workspaceToDelete, projectId);
        initialWorkspaceLayers.forEach((layer) => {
          kernel.current?.setLayer(layer.id, layer);
        });
        kernel.current.setProjectUIWorkspaceId(projectId, initialUIWorkspaceId);
        kernel.current.logUserAction('Workspace Deletion Undone', { restored_workspace_id: workspaceId, restored_workspace_name: workspaceToDelete.name });
        const restoreOperations: Array<Record<string, unknown>> = [
          {
            action: 'UPDATE',
            entity: 'StudioWorkspace',
            id: workspaceToDelete.id,
            payload: restoredWorkspacePayload,
          },
          ...restoreVersionIds.map((versionId) => ({
            action: 'UPDATE',
            entity: 'StudioWorkspaceVersion',
            id: versionId,
            payload: {
              projectId,
              workspaceId: workspaceToDelete.id,
            },
          })),
        ];
        void kernel.current.queueStudioWrite(
          restoreOperations as any
        ).catch((error) => {
          console.error(`[useWorkspaces.deleteWorkspace] Failed to restore workspace "${workspaceToDelete.id}".`, error);
        });
        // Deleting the workspace detached these rows on the server.
        (workspaceToDelete.layerIds ?? []).forEach((layerId) => {
          kernel.current?.queueLayerSave(layerId, { projectId, workspaceId: workspaceToDelete.id });
        });
      },
    });
  }, [kernel, getAllProjectWorkspaces, getLayersByWorkspaceId, syncProjectWorkspaceIdsDraft, buildWorkspaceMutationPayload]);


  const toggleWorkspaceExpanded = useCallback((workspaceId: string) => {
    if (!kernel.current) return;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useWorkspaces');
    kernel.current.setWorkspace(workspaceId, (workspace) => ({
      ...workspace,
      expanded: !workspace.expanded,
    }), projectId);
  }, [kernel]);

  const toggleWorkspaceLock = useCallback((workspaceId: string) => {
    if (!kernel.current) return;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useWorkspaces');
    kernel.current.setWorkspace(workspaceId, (workspace) => {
      const newLockedState = !(workspace.locked ?? true);
      kernel.current?.logUserAction('Workspace Lock Toggled', { workspace_id: workspaceId, new_state: newLockedState });
      return { ...workspace, locked: newLockedState };
    }, projectId);
  }, [kernel]);

  const toggleWorkspaceVisibility = useCallback((workspaceId: string) => {
    if (!kernel.current) return;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useWorkspaces');
    kernel.current.setWorkspace(workspaceId, (workspace) => {
      const newVisibleState = !(workspace.visible ?? true);
      kernel.current?.logUserAction('Workspace Visibility Toggled', { workspace_id: workspaceId, new_state: newVisibleState });
      return { ...workspace, visible: newVisibleState };
    }, projectId);
  }, [kernel]);

  const clearWorkspaceAnnotations = useCallback((workspaceId: string) => {
    if (!kernel.current) return;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useWorkspaces');
    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) return;

    const activeVersionId =
      typeof workspace.activeVersionId === 'string' && workspace.activeVersionId.trim().length > 0
        ? workspace.activeVersionId
        : (typeof workspace.validatedVersionId === 'string' && workspace.validatedVersionId.trim().length > 0
          ? workspace.validatedVersionId
          : null);
    if (!activeVersionId) return;
    const activeVersion = (workspace.versions ?? []).find((version) => version.id === activeVersionId);
    if (!activeVersion) return;

    const hasMask = Boolean(activeVersion.annotationMask) || Boolean(activeVersion.lastAnnotationMaskUrl);
    const hasComments = (activeVersion.annotationComments?.length ?? 0) > 0;

    if (!hasMask && !hasComments) {
      return;
    }

    const previousAnnotationMask = activeVersion.annotationMask ?? null;
    const previousAnnotationMaskIsDirty = activeVersion.annotationMaskIsDirty === true;
    const previousAnnotationComments = Array.isArray(activeVersion.annotationComments)
      ? activeVersion.annotationComments.map((comment) => ({ ...comment }))
      : activeVersion.annotationComments;
    const previousLastAnnotationMaskUrl = activeVersion.lastAnnotationMaskUrl;
    const hadWorkspaceCacheForVersion = workspace.annotationMaskVersionId === activeVersionId;
    const previousWorkspaceCache = hadWorkspaceCacheForVersion ? workspace.annotationMask : null;

    const performClear = () => {
      if (!kernel.current) return;
      kernel.current.setWorkspace(workspaceId, (currentWorkspace) => ({
        ...currentWorkspace,
        annotationMask:
          currentWorkspace.annotationMaskVersionId === activeVersionId
            ? null
            : currentWorkspace.annotationMask,
        annotationMaskVersionId:
          currentWorkspace.annotationMaskVersionId === activeVersionId
            ? activeVersionId
            : currentWorkspace.annotationMaskVersionId ?? null,
        versions: (currentWorkspace.versions ?? []).map((version) => {
          if (version.id !== activeVersionId) return version;
          return {
            ...version,
            annotationMask: null,
            annotationMaskIsDirty: true,
            annotationComments: [],
            lastAnnotationMaskUrl: undefined,
          };
        }),
      }), projectId);
      void kernel.current.saveWorkspaceVersionAnnotation(workspaceId, activeVersionId).catch((error) => {
        console.error(`[useWorkspaces.clearWorkspaceAnnotations] Failed to persist cleared annotation mask for version "${activeVersionId}".`, error);
      });
    };

    kernel.current.executeAction({
      description: 'Clear annotations',
      redo: () => {
        kernel.current?.logUserAction('Workspace Annotations Cleared', { workspace_id: workspaceId });
        performClear();
      },
      undo: () => {
        if (!kernel.current) return;
        kernel.current.setWorkspace(workspaceId, (currentWorkspace) => ({
          ...currentWorkspace,
          annotationMask:
            hadWorkspaceCacheForVersion
              ? previousWorkspaceCache ?? previousAnnotationMask
              : currentWorkspace.annotationMask,
          annotationMaskVersionId:
            hadWorkspaceCacheForVersion
              ? activeVersionId
              : currentWorkspace.annotationMaskVersionId ?? null,
          versions: (currentWorkspace.versions ?? []).map((version) => {
            if (version.id !== activeVersionId) return version;
            return {
              ...version,
              annotationMask: previousAnnotationMask,
              annotationMaskIsDirty: previousAnnotationMaskIsDirty,
              annotationComments: previousAnnotationComments,
              lastAnnotationMaskUrl: previousLastAnnotationMaskUrl,
            };
          }),
        }), projectId);
        kernel.current.logUserAction('Workspace Annotations Restored', { workspace_id: workspaceId });
      }
    });
  }, [kernel]);

  const switchAndEnsureWorkspace = useCallback(async (projectIdInput: string, workspaceName: string): Promise<string | undefined> => {
    if (!kernel.current) { return; }
    const projectId = projectIdInput.trim();
    if (!projectId) return;
    const trimmedName = workspaceName.trim();
    if (!trimmedName) {
        kernel.current?.handleError('switchAndEnsureWorkspace: workspace name cannot be empty.');
        throw new Error('Workspace name cannot be empty.');
    }

    const currentWorkspaces = getAllProjectWorkspaces(projectIdInput);
    let targetWorkspace = currentWorkspaces.find(ws => ws.name.toLowerCase() === trimmedName.toLowerCase());
    if (!targetWorkspace) {
        const newId = uuidv4();
        const newCanvasPos = { x: 0, y: 0, w: 720, h: 720 }; // A default position
        const newWorkspace: Workspace = {
            id: newId,
            projectId,
            name: trimmedName,
            layerIds: [],
            activeVersionId: null,
            generationRect: newCanvasPos,
            frameRect: { x: newCanvasPos.x, y: newCanvasPos.y, w: newCanvasPos.w, h: newCanvasPos.h },
            annotationMask: null,
            annotationMaskVersionId: null,
            annotationsEnabled: true,
            annotationOpaque: false,
            expanded: true,
            locked: true,
            visible: true,
            aspectRatio: 1,
            validatedVersionId: null,
            versionFilterMode: 'PRIMARY'
        };
      syncProjectWorkspaceIdsDraft(projectId, [...currentWorkspaces.map((workspace) => workspace.id), newId]);
      kernel.current.setWorkspace(newWorkspace.id, newWorkspace, projectId);
      persistWorkspaceCreate(newWorkspace, 'useWorkspaces.switchAndEnsureWorkspace');
      targetWorkspace = newWorkspace;
      console.log(`Workspace "${trimmedName}" created with ID ${newId}.`);
      kernel.current.logUserAction('Workspace Created by Script', { workspace_id: newId, workspace_name: trimmedName });
    }

    const currentActiveWorkspaceId = kernel.current.getProjectUIWorkspaceId(projectId);
    if (currentActiveWorkspaceId !== targetWorkspace.id) {
        kernel.current.logUserAction('Workspace Tab Changed', { new_workspace_id: targetWorkspace.id, old_workspace_id: currentActiveWorkspaceId });
        kernel.current.updateSelectedLayers(targetWorkspace.id, [], 'REPLACE');
        kernel.current.setProjectUIWorkspaceId(projectId, targetWorkspace.id);
    }

    return targetWorkspace.id;
  }, [kernel, getAllProjectWorkspaces, syncProjectWorkspaceIdsDraft, persistWorkspaceCreate]);

  const moveLayersToWorkspace = useCallback(async (projectIdInput: string, layerNamesOrIds: string[], targetWorkspaceId: string) => {
    const k = kernel.current;
    if (!k) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;
    const inventory = k.getInventory();
    const project = inventory.Project.registry[projectId];
    const workspaceIds = Array.isArray(project?.workspaceIds)
      ? project.workspaceIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
      : [];
    if (workspaceIds.length === 0) {
      k.handleError('Could not move layers: project has no workspace scope.');
      return;
    }

    const targetWorkspace = k.getWorkspace(targetWorkspaceId);
    if (!targetWorkspace || !workspaceIds.includes(targetWorkspaceId)) {
      console.error(`[moveLayersToWorkspace] Target workspace "${targetWorkspaceId}" is missing from project scope.`);
      k.handleError('Could not move layers: target workspace is missing from project.');
      return;
    }

    const projectLayerIds = Array.from(new Set(
      workspaceIds.flatMap((workspaceId) => {
        const workspace = k.getWorkspace(workspaceId);
        return Array.isArray(workspace?.layerIds) ? workspace.layerIds : [];
      })
    ));

    const layerRegistry = k.getLayerRegistry();
    const projectLayers = projectLayerIds
      .map((layerId) => layerRegistry[layerId])
      .filter((layer): layer is NonNullable<typeof layer> => Boolean(layer));

    const layersToMove = projectLayers.filter((layer) =>
      layerNamesOrIds.some((idOrName) => layer.id === idOrName || layer.name === idOrName)
    );

    if (layersToMove.length === 0) {
      k.handleError('No matching layers were found in the current project scope.');
      return;
    }

    const movedLayerIds = new Set(layersToMove.map((layer) => layer.id));
    const sourceWorkspaceIds = workspaceIds.filter((workspaceId) => {
      if (workspaceId === targetWorkspaceId) return false;
      const workspace = k.getWorkspace(workspaceId);
      const layerIds = Array.isArray(workspace?.layerIds) ? workspace.layerIds : [];
      return layerIds.some((layerId) => movedLayerIds.has(layerId));
    });

    layersToMove.forEach((layer) => {
      k.logUserAction('Layer Moved to Workspace', {
        layer_id: layer.id,
        source_workspace_id: layer.layerWorkspaceId,
        target_workspace_id: targetWorkspaceId,
      });
      k.setLayer(layer.id, {
        ...layer,
        layerWorkspaceId: targetWorkspaceId,
        mustRender: true,
      });
    });

    sourceWorkspaceIds.forEach((workspaceId) => {
      k.setWorkspace(workspaceId, (workspace) => ({
        ...workspace,
        layerIds: (workspace.layerIds ?? []).filter((layerId) => !movedLayerIds.has(layerId)),
      }), projectId);
    });

    k.setWorkspace(targetWorkspaceId, (workspace) => ({
      ...workspace,
      layerIds: [
        ...(workspace.layerIds ?? []).filter((layerId) => !movedLayerIds.has(layerId)),
        ...layersToMove.map((layer) => layer.id),
      ],
    }), projectId);

    k.setProjectUIWorkspaceId(projectId, targetWorkspaceId);
  }, [kernel]);

  const duplicateWorkspace = useCallback(async (projectIdInput: string, workspaceIdToDuplicate: string, newWorkspaceNames: string[], options?: { merge?: boolean, switchToNew?: boolean}): Promise<string | null> => {
    if (!kernel.current) return null;
    const projectId = projectIdInput.trim();
    if (!projectId) return null;
    const { merge = false, switchToNew = true } = options || {};

    if (newWorkspaceNames.length <= 0) return null;

    const currentWorkspaces = getAllProjectWorkspaces(projectIdInput);
    const sourceWorkspace = currentWorkspaces.find(ws => ws.id === workspaceIdToDuplicate);
    if (!sourceWorkspace) {
      console.error(`Workspace with ID "${workspaceIdToDuplicate}" not found for duplication.`);
      kernel.current?.handleError("Workspace to duplicate not found.");
      return null;
    }

    const sourceWorkspaceLayers = getLayersByWorkspaceId(workspaceIdToDuplicate);
    const workspacesToAdd: Workspace[] = [];
    const allNewLayers: Layer[] = [];
    const pendingRectsForLayout: Rect[] = [];

    let firstNewWorkspaceId: string | null = null;
    let firstNewFrameRect: Rect | null = null;
    const mergeToastId = merge
      ? toast.loading(`Preparing Duplicate & Merge for "${sourceWorkspace.name}"...`)
      : null;
    let mergedWorkspaceCount = 0;
    let mergedLayerSourceCount = 0;
    let skippedMergeWorkspaceCount = 0;
    
    const maxExistingName = currentWorkspaces
      .map(ws => parseInt(ws.name, 10))
      .filter(num => !isNaN(num))
      .reduce((max, num) => Math.max(max, num), 0);
    const projectRecord = kernel.current.getInventory().Project.registry[projectId] as Record<string, unknown> | undefined;
    const previousWorkspaceCounterRaw = projectRecord?.workspaceCounter;
    const previousWorkspaceCounter =
      typeof previousWorkspaceCounterRaw === 'number' && Number.isFinite(previousWorkspaceCounterRaw)
        ? previousWorkspaceCounterRaw
        : 0;
    let nextCount = Math.max(previousWorkspaceCounter, maxExistingName);
    const existingWorkspaceNames = new Set(
      currentWorkspaces
        .map((ws) => ws.name.trim().toLowerCase())
        .filter((name) => name.length > 0)
    );

    const getNextNumericWorkspaceName = (): string => {
      do {
        nextCount += 1;
      } while (existingWorkspaceNames.has(String(nextCount).toLowerCase()));
      return String(nextCount);
    };

    const resolveUniqueWorkspaceName = (requestedName: string | undefined): string => {
      const trimmedRequestedName = requestedName?.trim();
      const fallbackBase = `${sourceWorkspace.name} Copy`.trim();
      const baseName = trimmedRequestedName || fallbackBase || getNextNumericWorkspaceName();

      if (/^\d+$/.test(baseName)) {
        nextCount = Math.max(nextCount, parseInt(baseName, 10));
      }

      if (!existingWorkspaceNames.has(baseName.toLowerCase())) {
        existingWorkspaceNames.add(baseName.toLowerCase());
        return baseName;
      }

      if (/^\d+$/.test(baseName)) {
        const numericName = getNextNumericWorkspaceName();
        existingWorkspaceNames.add(numericName.toLowerCase());
        return numericName;
      }

      let suffix = 2;
      let candidate = `${baseName} ${suffix}`;
      while (existingWorkspaceNames.has(candidate.toLowerCase())) {
        suffix += 1;
        candidate = `${baseName} ${suffix}`;
      }
      existingWorkspaceNames.add(candidate.toLowerCase());
      return candidate;
    };

    for (let i = 0; i < newWorkspaceNames.length; i++) {
      const newWorkspaceName = resolveUniqueWorkspaceName(newWorkspaceNames[i]);
      
      const newWorkspaceId = uuidv4().substring(0, 8);
      let newFrameRect: Rect;

      const tempNewWorkspace = {
          frameRect: { ...sourceWorkspace.frameRect },
      };
      newFrameRect = findSmartPlacementPosition(projectId, tempNewWorkspace, pendingRectsForLayout);

      if (i === 0) {
        firstNewWorkspaceId = newWorkspaceId;
        firstNewFrameRect = newFrameRect;
      }

      const newWorkspace: Workspace = {
        id: newWorkspaceId,
        projectId,
        name: newWorkspaceName,
        layerIds: [],
        activeVersionId: null,
        generationRect: {
          x: newFrameRect.x + (sourceWorkspace.generationRect.x - sourceWorkspace.frameRect.x),
          y: newFrameRect.y + (sourceWorkspace.generationRect.y - sourceWorkspace.frameRect.y),
          w: sourceWorkspace.generationRect.w,
          h: sourceWorkspace.generationRect.h,
        },
        frameRect: newFrameRect,
        annotationMask: null,
        annotationMaskVersionId: null,
        annotationsEnabled: true,
        annotationOpaque: sourceWorkspace.annotationOpaque === true,
        expanded: true,
        locked: true,
        visible: true,
        aspectRatio: sourceWorkspace.aspectRatio,
        cameraState: sourceWorkspace.cameraState,
        validatedVersionId: null,
        versionFilterMode: 'PRIMARY'
      };
      workspacesToAdd.push(newWorkspace);
      pendingRectsForLayout.push(newFrameRect);

      if (merge) {
        try {
          const mergeCandidates = sortLayersByRenderOrder(
            sourceWorkspaceLayers.filter((layer) => layer.visible && (layer.imageData || isDuplicateMergeVectorLayer(layer)))
          );
          const layersToMerge: Layer[] = [];
          for (const candidateLayer of mergeCandidates) {
            if (candidateLayer.imageData) {
              layersToMerge.push(candidateLayer);
              continue;
            }
            toast.loading(
              `Rasterizing vector layer "${candidateLayer.name || candidateLayer.id}" for Duplicate & Merge...`,
              { id: mergeToastId ?? undefined }
            );
            try {
              const rasterizedLayer = await rasterizeSvgLayerForDuplicateMerge(candidateLayer);
              if (rasterizedLayer?.imageData) {
                layersToMerge.push(rasterizedLayer);
              }
            } catch (error) {
              console.warn(`[duplicateWorkspace] Failed to rasterize SVG layer "${candidateLayer.id}" before merge.`, error);
            }
          }
          if (layersToMerge.length > 0) {
            toast.loading(
              `Merging "${newWorkspaceName}" (${i + 1}/${newWorkspaceNames.length}) — ${layersToMerge.length} layer${layersToMerge.length > 1 ? 's' : ''} to flatten...`,
              { id: mergeToastId ?? undefined }
            );
            let mergedLayer = layersToMerge[0];
            if(!mergedLayer) continue;
            for (let j = 1; j < layersToMerge.length; j++) {
              let layerToMerge = layersToMerge[j];
              if(layerToMerge) {
                toast.loading(
                  `Merging "${newWorkspaceName}" — layer ${j + 1}/${layersToMerge.length}...`,
                  { id: mergeToastId ?? undefined }
                );
                mergedLayer = await kernel.current.mergeLayersCanvas(layerToMerge, mergedLayer);
              }
            }

            const finalMergedLayer: Layer = {
              ...mergedLayer,
              id: createLayerId(),
              timestamp: Date.now() + i,
              name: newWorkspaceName,
              imageSrc: undefined,
              type: undefined,
              params: undefined,
              layerWorkspaceId: newWorkspaceId,
              source: 'merge',
              mustRender: true,
              contentVersion: Date.now(),
              renderOrder: 0,
            };
            newWorkspace.layerIds = [finalMergedLayer.id];
            allNewLayers.push(finalMergedLayer);
            mergedWorkspaceCount += 1;
            mergedLayerSourceCount += layersToMerge.length;
          } else {
            skippedMergeWorkspaceCount += 1;
            toast.loading(
              `Duplicate & Merge: no visible mergeable layers found for "${newWorkspaceName}"...`,
              { id: mergeToastId ?? undefined }
            );
          }
        } catch (error) {
          toast.error(`Duplicate & Merge failed while flattening "${newWorkspaceName}".`, { id: mergeToastId ?? undefined });
          throw error;
        }
      } else {
        const layersToCopy = sortLayersByRenderOrder(sourceWorkspaceLayers);
        
        let timestampOffset = 0;
        const newLayersForThisCopy = layersToCopy.map((originalLayer): Layer => {
          const newId = createLayerId();
          timestampOffset++;
          return {
            ...originalLayer,
            id: newId,
            timestamp: Date.now() + (i * 1000) + timestampOffset,
            layerWorkspaceId: newWorkspaceId,
            contentVersion: Date.now(),
          };
        }).filter((l): l is Layer => l !== null);

        newWorkspace.layerIds = newLayersForThisCopy.map((layer) => layer.id);
        allNewLayers.push(...newLayersForThisCopy);
      }
    }

    if (workspacesToAdd.length > 0) {
      if (nextCount > previousWorkspaceCounter) {
        kernel.current.mergeEntityKernelState('Project', projectId, {
          workspaceCounter: nextCount,
        });
      }
      kernel.current.logUserAction('Workspaces Duplicated', {
        source_workspace_id: workspaceIdToDuplicate,
        count: workspacesToAdd.length,
        merged: merge,
        switched_to_new: switchToNew
      });

      syncProjectWorkspaceIdsDraft(projectId, [
        ...currentWorkspaces.map((workspace) => workspace.id),
        ...workspacesToAdd.map((workspace) => workspace.id),
      ]);
      workspacesToAdd.forEach((workspace) => {
        kernel.current?.setWorkspace(workspace.id, workspace, projectId);
      });
      if (allNewLayers.length > 0) {
        allNewLayers.forEach((layer) => {
          kernel.current?.setLayer(layer.id, layer);
        });
      }
      persistDuplicatedWorkspaceBundle(workspacesToAdd, 'useWorkspaces.duplicateWorkspace');
      if (mergeToastId !== null) {
        if (mergedWorkspaceCount > 0) {
          toast.success(
            `Duplicate & Merge complete: ${mergedWorkspaceCount} workspace${mergedWorkspaceCount > 1 ? 's' : ''} flattened from ${mergedLayerSourceCount} layer${mergedLayerSourceCount > 1 ? 's' : ''}.`,
            { id: mergeToastId }
          );
        } else {
          toast.warning(
            `Duplicate created, but no visible mergeable layers were available${skippedMergeWorkspaceCount > 1 ? ` in ${skippedMergeWorkspaceCount} workspaces` : ''}.`,
            { id: mergeToastId }
          );
        }
      }
      
      if (switchToNew && firstNewWorkspaceId && firstNewFrameRect) {
        const wasActive = kernel.current.getProjectUIWorkspaceId(projectId) === workspaceIdToDuplicate;
        kernel.current.setProjectUIWorkspaceId(projectId, firstNewWorkspaceId);

        if (wasActive) {
          const currentZoom = getProjectZoomLevel(kernel.current, projectId);
          const currentPan = getProjectPanOffset(kernel.current, projectId);
          
          const sourceRect = sourceWorkspace.frameRect;
          const targetRect = firstNewFrameRect;

          const deltaWorldX = targetRect.x - sourceRect.x;
          const deltaWorldY = targetRect.y - sourceRect.y;

          const newPan = {
            x: currentPan.x - (deltaWorldX * currentZoom),
            y: currentPan.y - (deltaWorldY * currentZoom),
          };

          kernel.current.applyViewportTransform(newPan, currentZoom);
          setProjectPanOffset(kernel.current, projectId, newPan);
        } else {
          kernel.current?.zoomToFitRectInViewport(projectId, firstNewFrameRect, 1.2, kernel.current?.getSpectacleMode());
        }
      }

      return firstNewWorkspaceId;
    }
    return null;
  }, [kernel, findSmartPlacementPosition, getAllProjectWorkspaces, getLayersByWorkspaceId, syncProjectWorkspaceIdsDraft, persistDuplicatedWorkspaceBundle]);


  const fitWorkspaceFrameToContent = useCallback(async (projectIdInput: string, workspaceId: string, handleType: ResizeHandleType) => {
    if (!kernel.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;

    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace || workspace.locked || !handleType) {
      return;
    }

    const layersInWorkspace = getLayersByWorkspaceId(workspaceId).filter((layer) => layer.visible);

    if (layersInWorkspace.length === 0) {
      return;
    }

    const unionResult = await kernel.current.calculateUnionRectOfLayers(layersInWorkspace);
    if (!unionResult || !unionResult.unionRect) {
      return;
    }

    const contentRect = unionResult.unionRect;
    const initialFrameRect = { ...workspace.frameRect };
    const targetFrameRect = { ...workspace.frameRect };

    // Calculate target values based on handleType
    switch (handleType) {
      case 'topLeft':
        targetFrameRect.w = (initialFrameRect.x + initialFrameRect.w) - contentRect.x;
        targetFrameRect.h = (initialFrameRect.y + initialFrameRect.h) - contentRect.y;
        targetFrameRect.x = contentRect.x;
        targetFrameRect.y = contentRect.y;
        break;
      case 'top':
        targetFrameRect.h = (initialFrameRect.y + initialFrameRect.h) - contentRect.y;
        targetFrameRect.y = contentRect.y;
        break;
      case 'topRight':
        targetFrameRect.w = contentRect.x + contentRect.w - initialFrameRect.x;
        targetFrameRect.h = (initialFrameRect.y + initialFrameRect.h) - contentRect.y;
        targetFrameRect.y = contentRect.y;
        break;
      case 'left':
        targetFrameRect.w = (initialFrameRect.x + initialFrameRect.w) - contentRect.x;
        targetFrameRect.x = contentRect.x;
        break;
      case 'right':
        targetFrameRect.w = contentRect.x + contentRect.w - initialFrameRect.x;
        break;
      case 'bottomLeft':
        targetFrameRect.w = (initialFrameRect.x + initialFrameRect.w) - contentRect.x;
        targetFrameRect.h = contentRect.y + contentRect.h - initialFrameRect.y;
        targetFrameRect.x = contentRect.x;
        break;
      case 'bottom':
        targetFrameRect.h = contentRect.y + contentRect.h - initialFrameRect.y;
        break;
      case 'bottomRight':
        targetFrameRect.w = contentRect.x + contentRect.w - initialFrameRect.x;
        targetFrameRect.h = contentRect.y + contentRect.h - initialFrameRect.y;
        break;
      case 'move':
        break;
    }
  
    // Ensure minimum dimensions
    if (targetFrameRect.w < 50) targetFrameRect.w = 50;
    if (targetFrameRect.h < 50) targetFrameRect.h = 50;

    const deltaX = targetFrameRect.x - initialFrameRect.x;
    const deltaY = targetFrameRect.y - initialFrameRect.y;

    // Update layer positions first
    if (Math.abs(deltaX) > 0.01 || Math.abs(deltaY) > 0.01) {
      kernel.current.invalidateWorkspaceVersionCache(workspaceId);
      getLayersByWorkspaceId(workspaceId).forEach((layer) => {
        kernel.current?.setLayer(layer.id, {
          ...layer,
          rect: { ...layer.rect, x: layer.rect.x - deltaX, y: layer.rect.y - deltaY },
        });
      });
    }

    // Update workspace frame (and annotation mask)
    kernel.current.setWorkspaceFrameRect(workspaceId, targetFrameRect);
    kernel.current.invalidateWorkspaceVersionCache(workspaceId);

    // Update generation rect if needed
    const frame = targetFrameRect;
    const canvas = workspace.generationRect;
    const newCanvasX = Math.max(frame.x, canvas.x);
    const newCanvasY = Math.max(frame.y, canvas.y);
    const newCanvasMaxX = Math.min(frame.x + frame.w, canvas.x + canvas.w);
    const newCanvasMaxY = Math.min(frame.y + frame.h, canvas.y + canvas.h);
    const newCanvasW = Math.max(1, newCanvasMaxX - newCanvasX);
    const newCanvasH = Math.max(1, newCanvasMaxY - newCanvasY);

    if (kernel.current && workspaceId === kernel.current.getProjectUIWorkspaceId(projectId)) {
      const dx = newCanvasX - canvas.x;
      const dy = newCanvasY - canvas.y;
      if (dx !== 0 || dy !== 0 || newCanvasW !== canvas.w || newCanvasH !== canvas.h) {
        await kernel.current.setGenerationArea(workspaceId, newCanvasX, newCanvasY, newCanvasW, newCanvasH);
      }
    }
  }, [kernel, getLayersByWorkspaceId]);

  const fitWorkspaceFrameToGenerationArea = useCallback((projectIdInput: string, workspaceId: string) => {
    if (!kernel.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;

    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) return;

    const currentFrameRect = workspace.frameRect;
    const targetFrameRect = workspace.generationRect;
    const dx = targetFrameRect.x - currentFrameRect.x;
    const dy = targetFrameRect.y - currentFrameRect.y;
    const didFrameChange =
      Math.abs(dx) > 0.01 ||
      Math.abs(dy) > 0.01 ||
      Math.abs(targetFrameRect.w - currentFrameRect.w) > 0.01 ||
      Math.abs(targetFrameRect.h - currentFrameRect.h) > 0.01;

    if (!didFrameChange) return;

    kernel.current.invalidateWorkspaceVersionCache(workspaceId);

    if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) {
      getLayersByWorkspaceId(workspaceId).forEach((layer) => {
        kernel.current?.setLayer(layer.id, {
          ...layer,
          rect: { ...layer.rect, x: layer.rect.x - dx, y: layer.rect.y - dy },
        });
      });
    }

    kernel.current.setWorkspaceFrameRect(workspaceId, { ...targetFrameRect });
    kernel.current.invalidateWorkspaceVersionCache(workspaceId);
    kernel.current.logUserAction('Workspace Frame Fit to Generation Area', {
      workspace_id: workspaceId,
    });

    const updatedWorkspace = kernel.current.getWorkspace(workspaceId);
    if (updatedWorkspace) {
      kernel.current.zoomToFitRectInViewport(projectId, updatedWorkspace.frameRect, 1.2, kernel.current.getSpectacleMode());
    }
  }, [kernel, getLayersByWorkspaceId]);

  const forceFrameRatioOnContent = useCallback(async (projectIdInput: string, workspaceId: string, ratio: number) => {
    if(!kernel.current) throw "No api";
    const projectId = projectIdInput.trim();
    if (!projectId) return;
    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) return;

    if (!Number.isFinite(ratio) || ratio <= 0) return;

    const currentFrameRect = { ...workspace.frameRect };
    if (
      !Number.isFinite(currentFrameRect.x) ||
      !Number.isFinite(currentFrameRect.y) ||
      !Number.isFinite(currentFrameRect.w) ||
      !Number.isFinite(currentFrameRect.h) ||
      currentFrameRect.w <= 0 ||
      currentFrameRect.h <= 0
    ) return;

    const layersInWorkspace = getLayersByWorkspaceId(workspaceId).filter((layer) => layer.visible);
    const unionResult = layersInWorkspace.length > 0
      ? await kernel.current.calculateUnionRectOfLayers(layersInWorkspace)
      : null;
    const contentRect = unionResult?.unionRect ?? null;

    let targetFrameRect: Rect;

    if (contentRect && contentRect.w > 0 && contentRect.h > 0) {
      const contentRatio = contentRect.w / contentRect.h;
      let newFrameW, newFrameH, newFrameX, newFrameY;

      if (contentRatio > ratio) {
        newFrameW = contentRect.w;
        newFrameH = newFrameW / ratio;
        newFrameX = contentRect.x;
        newFrameY = contentRect.y - (newFrameH - contentRect.h) / 2;
      } else {
        newFrameH = contentRect.h;
        newFrameW = newFrameH * ratio;
        newFrameY = contentRect.y;
        newFrameX = contentRect.x - (newFrameW - contentRect.w) / 2;
      }

      targetFrameRect = { x: newFrameX, y: newFrameY, w: newFrameW, h: newFrameH };
    } else {
      const preservedArea = currentFrameRect.w * currentFrameRect.h;
      const newFrameW = Math.sqrt(preservedArea * ratio);
      const newFrameH = preservedArea / newFrameW;
      targetFrameRect = {
        x: currentFrameRect.x,
        y: currentFrameRect.y,
        w: newFrameW,
        h: newFrameH,
      };
    }

    const targetGenerationRect = { ...targetFrameRect };

    anime({
      targets: currentFrameRect,
      x: targetFrameRect.x,
      y: targetFrameRect.y,
      w: targetFrameRect.w,
      h: targetFrameRect.h,
      duration: 200,
      easing: 'easeInOutSine',
      update: () => {
        // During animation, we just update the visual frame rect. 
        // We do NOT call setWorkspaceFrameRect here because it's heavy (mask reprojection).
        kernel.current?.setWorkspace(workspaceId, (currentWorkspace) => ({
          ...currentWorkspace,
          frameRect: { ...currentFrameRect },
        }), projectId);
        kernel.current?.invalidateWorkspaceVersionCache(workspaceId);
      },
      complete: () => {
        // Final commit: Use setWorkspaceFrameRect to ensure mask is reprojected correctly
        kernel.current?.setWorkspaceFrameRect(workspaceId, targetFrameRect);

        // Then handle generation area
        const postFrameWorkspace = kernel.current?.getWorkspace(workspaceId);
        if (postFrameWorkspace && kernel.current && workspaceId === kernel.current.getProjectUIWorkspaceId(projectId)) {
          const oldGenerationRect = postFrameWorkspace.generationRect;
          const dx = targetGenerationRect.x - oldGenerationRect.x;
          const dy = targetGenerationRect.y - oldGenerationRect.y;
          if (dx !== 0 || dy !== 0 || targetGenerationRect.w !== oldGenerationRect.w || targetGenerationRect.h !== oldGenerationRect.h) {
            kernel.current.setGenerationArea(workspaceId, targetGenerationRect.x, targetGenerationRect.y, targetGenerationRect.w, targetGenerationRect.h);
          }
        }
        kernel.current?.invalidateWorkspaceVersionCache(workspaceId);
        kernel.current?.logUserAction('Workspace Frame Ratio Forced', { workspace_id: workspaceId, ratio: ratio });
        kernel.current?.zoomToFitRectInViewport(projectId, targetFrameRect, 1.2, kernel.current?.getSpectacleMode());
      }
    });
  }, [kernel, getLayersByWorkspaceId]);

  const calculateNewRectForResize = useCallback((
    currentState: CanvasResizeState,
    currentPosition: Rect,
    currentAspectRatio: number | null,
    frameRect: Rect
  ): Rect => {
    const { handleType, delta, isShiftPressed, initialRect } = currentState;
    const { w: oldW, h: oldH, x: oldX, y: oldY } = currentPosition;
    let { dx, dy } = delta;

    if (handleType === 'move') {
      if (!initialRect) return currentPosition;
      const newX = initialRect.x + dx;
      const newY = initialRect.y + dy;

      // Clamp to frameRect to prevent dragging it outside its workspace frame
      const clampedX = Math.max(frameRect.x, Math.min(newX, frameRect.x + frameRect.w - initialRect.w));
      const clampedY = Math.max(frameRect.y, Math.min(newY, frameRect.y + frameRect.h - initialRect.h));

      return {
        ...initialRect,
        x: clampedX,
        y: clampedY,
      };
    }

    // Clamp the delta to prevent resizing beyond the frameRect boundaries.
    switch (handleType) {
      case 'right':
        dx = Math.min(dx, frameRect.x + frameRect.w - (oldX + oldW));
        break;
      case 'left':
        dx = Math.max(dx, frameRect.x - oldX);
        break;
      case 'bottom':
        dy = Math.min(dy, frameRect.y + frameRect.h - (oldY + oldH));
        break;
      case 'top':
        dy = Math.max(dy, frameRect.y - oldY);
        break;
      case 'bottomRight':
        dx = Math.min(dx, frameRect.x + frameRect.w - (oldX + oldW));
        dy = Math.min(dy, frameRect.y + frameRect.h - (oldY + oldH));
        break;
      case 'bottomLeft':
        dx = Math.max(dx, frameRect.x - oldX);
        dy = Math.min(dy, frameRect.y + frameRect.h - (oldY + oldH));
        break;
      case 'topRight':
        dx = Math.min(dx, frameRect.x + frameRect.w - (oldX + oldW));
        dy = Math.max(dy, frameRect.y - oldY);
        break;
      case 'topLeft':
        dx = Math.max(dx, frameRect.x - oldX);
        dy = Math.max(dy, frameRect.y - oldY);
        break;
      case null:
        break;
    }


    let newW = oldW;
    let newH = oldH;
    let newX = oldX;
    let newY = oldY;

    // --- Base Calculation (from the stable version) ---
    if (currentAspectRatio) {
      const isCentered = isShiftPressed && ['top', 'bottom', 'left', 'right'].includes(handleType || '');
      if (isCentered) {
        const change = (handleType === 'left' || handleType === 'right') ? dx : dy;
        const sign = (handleType === 'left' || handleType === 'top') ? -1 : 1;
        newW = oldW + 2 * change * sign;
        newH = newW / currentAspectRatio;
        newX = oldX + (oldW - newW) / 2;
        newY = oldY + (oldH - newH) / 2;
      } else {
        switch (handleType) {
          case 'bottomRight': { const targetW = oldW + dx; const targetH = oldH + dy; if (targetW / targetH > currentAspectRatio) { newW = targetW; newH = newW / currentAspectRatio; } else { newH = targetH; newW = newH * currentAspectRatio; } break; }
          case 'bottomLeft': { const targetW = oldW - dx; const targetH = oldH + dy; if (targetW / targetH > currentAspectRatio) { newW = targetW; newH = newW / currentAspectRatio; } else { newH = targetH; newW = newH * currentAspectRatio; } newX = oldX + oldW - newW; break; }
          case 'topRight': { const targetW = oldW + dx; const targetH = oldH - dy; if (targetW / targetH > currentAspectRatio) { newW = targetW; newH = newW / currentAspectRatio; } else { newH = targetH; newW = newH * currentAspectRatio; } newY = oldY + oldH - newH; break; }
          case 'topLeft': { const targetW = oldW - dx; const targetH = oldH - dy; if (targetW / targetH > currentAspectRatio) { newW = targetW; newH = newW / currentAspectRatio; } else { newH = targetH; newW = newH * currentAspectRatio; } newX = oldX + oldW - newW; newY = oldY + oldH - newH; break; }
          case 'right': newW = oldW + dx; newH = newW / currentAspectRatio; newY = oldY + (oldH - newH) / 2; break;
          case 'left': newW = oldW - dx; newH = newW / currentAspectRatio; newX = oldX + dx; newY = oldY + (oldH - newH) / 2; break;
          case 'bottom': newH = oldH + dy; newW = newH * currentAspectRatio; newX = oldX + (oldW - newW) / 2; break;
          case 'top': newH = oldH - dy; newW = newH * currentAspectRatio; newY = oldY + dy; newX = oldX + (oldW - newW) / 2; break;
          case null: break;
        }
      }
    } else { // Free-form
      if (isShiftPressed && ['top', 'bottom', 'left', 'right'].includes(handleType || '')) {
        const change = (handleType === 'left' || handleType === 'right') ? dx : dy;
        const sign = (handleType === 'left' || handleType === 'top') ? -1 : 1;
        newW = oldW + 2 * change * sign; newH = oldH + 2 * change * sign;
        newX = oldX - change * sign; newY = oldY - change * sign;
      } else {
        if (handleType === 'topLeft') { newW = oldW - dx; newH = oldH - dy; newX = oldX + dx; newY = oldY + dy; }
        else if (handleType === 'topRight') { newW = oldW + dx; newH = oldH - dy; newY = oldY + dy; }
        else if (handleType === 'bottomLeft') { newW = oldW - dx; newH = oldH + dy; newX = oldX + dx; }
        else if (handleType === 'bottomRight') { newW = oldW + dx; newH = oldH + dy; }
        else if (handleType === 'top') { newH = oldH - dy; newY = oldY + dy; }
        else if (handleType === 'bottom') { newH = oldH + dy; }
        else if (handleType === 'left') { newW = oldW - dx; newX = oldX + dx; }
        else if (handleType === 'right') { newW = oldW + dx; }
      }
    }

    // --- Inversion Handling ---
    if (newW < 0) {
      newX = newX + newW; // Move the origin to the other side
      newW = Math.abs(newW); // Make width positive
    }
    if (newH < 0) {
      newY = newY + newH; // Move the origin to the other side
      newH = Math.abs(newH); // Make height positive
    }

    return {
      w: newW,
      h: newH,
      x: newX,
      y: newY,
    };
  }, []);


  const expandWorkspaceFrame = useCallback((projectIdInput: string, workspaceId: string, direction: 'top' | 'right' | 'bottom' | 'left', amount: number) => {
    if (!kernel.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;

    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) {
      console.error(`expandWorkspaceFrame: Workspace with ID "${workspaceId}" not found.`);
      kernel.current?.handleError("Workspace to expand not found.");
      return;
    }

    kernel.current?.invalidateWorkspaceVersionCache(workspaceId);

    const newFrameRect = { ...workspace.frameRect };

    switch (direction) {
      case 'top':
        newFrameRect.y -= amount;
        newFrameRect.h += amount;
        break;
      case 'bottom':
        newFrameRect.h += amount;
        break;
      case 'left':
        newFrameRect.x -= amount;
        newFrameRect.w += amount;
        break;
      case 'right':
        newFrameRect.w += amount;
        break;
    }

    if (newFrameRect.w < 50) newFrameRect.w = 50;
    if (newFrameRect.h < 50) newFrameRect.h = 50;

    // 1. Update Frame Rect (and reproject Annotation Mask)
    kernel.current?.setWorkspaceFrameRect(workspaceId, newFrameRect);

    // 2. Update Generation Area (and reproject Generation Mask)
    // For outpainting, the canvas must expand with the frame.
    const newGenerationRect = { ...newFrameRect };

    if (kernel.current && workspaceId === kernel.current.getProjectUIWorkspaceId(projectId)) {
      kernel.current.setGenerationArea(workspaceId, newGenerationRect.x, newGenerationRect.y, newGenerationRect.w, newGenerationRect.h);
    }

    kernel.current?.logUserAction('Workspace Frame Expanded', {
      workspace_id: workspaceId,
      direction,
      amount,
    });

    // Zoom to fit
    const updatedWorkspace = kernel.current?.getWorkspace(workspaceId);
    if (updatedWorkspace) {
      kernel.current?.zoomToFitRectInViewport(projectId, updatedWorkspace.frameRect, 1.2, kernel.current?.getSpectacleMode());
    }
  }, [kernel]);

  const setWorkspaceAspectRatio = useCallback((projectIdInput: string, workspaceId: string, ratio: AspectRatio) => {
    if (!kernel.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;

    const spectacleMode = kernel.current.getSpectacleMode();
    const currentWorkspace = kernel.current.getWorkspace(workspaceId);
    if (!currentWorkspace) return;

    kernel.current.invalidateWorkspaceVersionCache(workspaceId);

    const { frameRect, generationRect: oldRect } = currentWorkspace;

    // If we are removing the aspect ratio, we only update the property and do not change the rect.
    if (ratio === null) {
        kernel.current.setWorkspace(workspaceId, (workspace) => ({
          ...workspace,
          aspectRatio: null,
        }), projectId);
        return;
    }

    // --- Step 1: Calculate the ideal new rect based on preserving area and center ---
    const centerX = oldRect.x + oldRect.w / 2;
    const centerY = oldRect.y + oldRect.h / 2;
    const currentArea = oldRect.w * oldRect.h;

    if (currentArea <= 0) { // Cannot calculate if there's no area
        kernel.current.setWorkspace(workspaceId, (workspace) => ({
          ...workspace,
          aspectRatio: ratio,
        }), projectId);
        return;
    }

    const idealHeight = Math.sqrt(currentArea / ratio);
    const idealWidth = idealHeight * ratio;
    
    // --- Step 2: Clamp and adjust the ideal rect to fit within the frameRect ---
    let finalWidth = idealWidth;
    let finalHeight = idealHeight;

    // If the ideal rect is wider or taller than the frame, we must scale it down.
    if (finalWidth > frameRect.w || finalHeight > frameRect.h) {
      const widthScale = frameRect.w / finalWidth;
      const heightScale = frameRect.h / finalHeight;
      // Use the smaller scale factor to ensure it fits in both dimensions while maintaining the ratio
      const scale = Math.min(widthScale, heightScale);
      finalWidth *= scale;
      finalHeight *= scale;
    }

    // --- Step 3: Position the final, clamped rect, preserving center ---
    let finalX = centerX - finalWidth / 2;
    let finalY = centerY - finalHeight / 2;

    // Clamp position to be within frameRect
    finalX = Math.max(frameRect.x, finalX);
    if (finalX + finalWidth > frameRect.x + frameRect.w) {
        finalX = frameRect.x + frameRect.w - finalWidth;
    }
    finalY = Math.max(frameRect.y, finalY);
    if (finalY + finalHeight > frameRect.y + frameRect.h) {
        finalY = frameRect.y + frameRect.h - finalHeight;
    }

    const finalRect = { x: finalX, y: finalY, w: finalWidth, h: finalHeight };

    // --- Step 4: Apply the changes ---
    // First, update the aspect ratio on the workspace.
    kernel.current.setWorkspace(workspaceId, (workspace) => ({
      ...workspace,
      aspectRatio: ratio,
    }), projectId);

    // Then, call setGenerationArea which handles mask reprojection.
    // suppressSaveState=true keeps this flow kernel-autosave friendly.
    kernel.current.setGenerationArea(workspaceId, finalRect.x, finalRect.y, finalRect.w, finalRect.h, true);

    // --- Step 5: Handle animation ---
    if (spectacleMode && workspaceId === kernel.current.getProjectUIWorkspaceId(projectId)) {
      const animationProps = { ...oldRect };
      kernel.current.setWorkspaceAnimatedGenerationRect(workspaceId, { ...animationProps });

      anime({
        targets: animationProps,
        x: finalRect.x, y: finalRect.y, w: finalRect.w, h: finalRect.h,
        duration: 240,
        easing: 'easeInOutSine',
        update: () => kernel.current?.setWorkspaceAnimatedGenerationRect(workspaceId, { ...animationProps }),
        complete: () => kernel.current?.setWorkspaceAnimatedGenerationRect(workspaceId, null)
      });
    }
  }, [kernel]);

  return useMemo(() => ({
    getProjectUIWorkspaceId,
    setProjectUIWorkspaceId,
    setWorkspaceAnimatedGenerationRect,
    loadWorkspace,
    persistWorkspaceCreate,
    handleAddWorkspace,
    renameWorkspace,
    deleteWorkspace,
    toggleWorkspaceExpanded,
    toggleWorkspaceLock,
    toggleWorkspaceVisibility,
    switchAndEnsureWorkspace,
    moveLayersToWorkspace,
    duplicateWorkspace,
    fitWorkspaceFrameToContent,
    fitWorkspaceFrameToGenerationArea,
    forceFrameRatioOnContent,
    calculateNewRectForResize,
    expandWorkspaceFrame,
    setWorkspaceAspectRatio,
    setWorkspaceFrameRect,
    clearWorkspaceAnnotations
  }), [
    getProjectUIWorkspaceId,
    setProjectUIWorkspaceId,
    setWorkspaceAnimatedGenerationRect,
    loadWorkspace,
    persistWorkspaceCreate,
    handleAddWorkspace,
    renameWorkspace,
    deleteWorkspace,
    toggleWorkspaceExpanded,
    toggleWorkspaceLock,
    toggleWorkspaceVisibility,
    switchAndEnsureWorkspace,
    moveLayersToWorkspace,
    duplicateWorkspace,
    fitWorkspaceFrameToContent,
    fitWorkspaceFrameToGenerationArea,
    forceFrameRatioOnContent,
    calculateNewRectForResize,
    expandWorkspaceFrame,
    setWorkspaceAspectRatio,
    setWorkspaceFrameRect,
    clearWorkspaceAnnotations
  ]);
};

